import React, { useState } from "react";
import SidebarAdmin from "./SidebarAdmin";
import AdminNavbar from "./AdminNavbar";
import PostAdminButton from "../Assets/PostAdminButton";
import ModalConfirmation from "../Assets/ModalConfirmation";
import AdminSearchBar from "../Assets/AdminSearchBar";
import BlogModal from "./BlogModal";
import useBlog from "../Hooks/useBlog";
import { BlogPost } from "../Types/Types";
import { useTranslation } from "react-i18next";

interface AdminBlogProps {
  lang: string;
}

const AdminBlog: React.FC<AdminBlogProps> = ({ lang }) => {
  const { t } = useTranslation();
  const { blogs, loading, error, searchQuery, setSearchQuery, createBlog, updateBlog, deleteBlog } = useBlog();

  const [showForm, setShowForm] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<BlogPost | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleDeleteConfirm = async () => {
    if (selectedId) await deleteBlog(selectedId);
    setSelectedId(null);
    setModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex font-montserrat">
      <div className="w-64">
        <SidebarAdmin />
      </div>

      <div className="flex-1 flex flex-col">
        <AdminNavbar />
        <div className="flex-1 py-12 px-8 lg:px-24 space-y-10">
          <h1 className="text-4xl text-brand-800">{t("navbar.health")}</h1>

          <div className="flex justify-between items-center space-x-4">
            <PostAdminButton onClick={() => { setItemToEdit(null); setShowForm(true); }} />
            <AdminSearchBar searchQuery={searchQuery} onSearch={setSearchQuery} />
          </div>

          {loading ? (
            <p className="text-gray-500">Loading…</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-gray-50 text-sm text-gray-600">
                  <tr>
                    <th className="p-4">{t("tableHeaders.image")}</th>
                    <th className="p-4">{t("forms.title")}</th>
                    <th className="p-4">{t("tableHeaders.createdAt")}</th>
                    <th className="p-4 text-right">{t("tableHeaders.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((item) => (
                    <tr key={item._id} className="border-t border-gray-100">
                      <td className="p-4">
                        {item.image ? (
                          <img src={item.image} alt="" className="h-12 w-16 rounded object-cover" />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="p-4 font-medium text-gray-800">{item.title?.[lang] || item.title?.en}</td>
                      <td className="p-4 text-sm text-gray-500">
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ""}
                      </td>
                      <td className="p-4 text-right space-x-3">
                        <button
                          onClick={() => { setItemToEdit(item); setShowForm(true); }}
                          className="text-brand-600 hover:underline"
                        >
                          ✎
                        </button>
                        <button
                          onClick={() => { setSelectedId(item._id); setModalOpen(true); }}
                          className="text-red-500 hover:underline"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                  {blogs.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-6 text-center text-gray-500">{t("blog.empty")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <BlogModal
          isOpen={showForm}
          initialData={itemToEdit || undefined}
          onClose={() => { setShowForm(false); setItemToEdit(null); }}
          onSubmit={async (formData) => {
            if (itemToEdit) await updateBlog(itemToEdit._id, formData);
            else await createBlog(formData);
            setShowForm(false);
            setItemToEdit(null);
          }}
        />
      )}

      <ModalConfirmation
        isOpen={modalOpen}
        onClose={() => { setSelectedId(null); setModalOpen(false); }}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default AdminBlog;
